import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { SectionEyebrow } from '../../../components/ui/SectionEyebrow';
import { useAuth, setPostAuthRedirect } from '../../../lib/auth/AuthContext';
import { GoogleOAuthIcon, MicrosoftOAuthIcon } from '../components/OAuthBrandIcons';

const schema = z.object({
  email: z.string().email('Enter a valid email'),
  password: z.string().min(8, 'Use at least 8 characters')
});

type FormValues = z.infer<typeof schema>;

function normalizeRedirect(raw: string | null): string {
  if (!raw || !raw.startsWith('/')) return '/app/explore';
  if (raw.startsWith('//')) return '/app/explore';
  return raw;
}

export const AuthScreen: React.FC = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const redirectTo = normalizeRedirect(params.get('redirect'));
  const { signInWithEmail, signUpWithEmail, signInWithOAuth, initialized, session } = useAuth();
  const [mode, setMode] = useState<'sign_in' | 'sign_up'>('sign_in');
  const [formError, setFormError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '' }
  });

  useEffect(() => {
    if (!initialized) return;
    if (session) {
      navigate(redirectTo, { replace: true });
    }
  }, [initialized, session, navigate, redirectTo]);

  const onOAuth = async (provider: 'google' | 'azure') => {
    setFormError(null);
    setInfo(null);
    setPostAuthRedirect(redirectTo);
    setPending(true);
    const { error } = await signInWithOAuth(provider);
    setPending(false);
    if (error) setFormError(error.message);
  };

  const onSubmit = handleSubmit(async (data) => {
    setFormError(null);
    setInfo(null);
    setPending(true);
    if (mode === 'sign_in') {
      const { error } = await signInWithEmail(data.email, data.password);
      setPending(false);
      if (error) {
        setFormError(error.message);
        return;
      }
      navigate(redirectTo, { replace: true });
      return;
    }

    const { error, session, alreadyRegistered, needsEmailConfirmation } = await signUpWithEmail(
      data.email,
      data.password
    );
    setPending(false);
    if (error) {
      setFormError(error.message);
      return;
    }
    if (session) {
      navigate(redirectTo, { replace: true });
      return;
    }
    if (alreadyRegistered) {
      setFormError('An account with this email already exists. Sign in instead, or use a different email.');
      setMode('sign_in');
      return;
    }
    if (needsEmailConfirmation) {
      setInfo('Check your email to confirm your account, then sign in. If nothing arrives, check spam or try again in a few minutes.');
      return;
    }
    setFormError('Could not create your account. Try signing in, or use a different email.');
  });

  return (
    <div className="flex w-full flex-1 flex-col bg-background px-4 pb-12 pt-10 sm:px-8 sm:pb-14 sm:pt-14 lg:mx-auto lg:max-w-md lg:px-0">
      <div className="space-y-8">
        <header className="space-y-2">
          <SectionEyebrow>Account</SectionEyebrow>
          <h1 className="font-display text-2xl font-semibold leading-snug text-text sm:text-[1.65rem]">
            Sign in to Newrotune
          </h1>
          <p className="text-sm leading-relaxed text-textSecondary">
            Use email or a provider you already trust. One account type for now; subscription status is stored on your
            profile.
          </p>
        </header>

        <div className="flex gap-2 rounded-full border border-borderSoft/60 bg-surface/50 p-1">
          <button
            type="button"
            className={[
              'flex-1 rounded-full py-2 text-xs font-semibold transition',
              mode === 'sign_in' ? 'bg-white text-brandDeep shadow-sm' : 'text-textMuted hover:text-text'
            ].join(' ')}
            onClick={() => {
              setMode('sign_in');
              setFormError(null);
              setInfo(null);
            }}
          >
            Sign in
          </button>
          <button
            type="button"
            className={[
              'flex-1 rounded-full py-2 text-xs font-semibold transition',
              mode === 'sign_up' ? 'bg-white text-brandDeep shadow-sm' : 'text-textMuted hover:text-text'
            ].join(' ')}
            onClick={() => {
              setMode('sign_up');
              setFormError(null);
              setInfo(null);
            }}
          >
            Create account
          </button>
        </div>

        <div className="space-y-3">
          <Button
            type="button"
            variant="outline"
            fullWidth
            size="lg"
            className="inline-flex items-center justify-center gap-3 rounded-2xl py-3.5 text-sm font-semibold"
            disabled={pending}
            onClick={() => onOAuth('google')}
          >
            <GoogleOAuthIcon />
            <span>Continue with Google</span>
          </Button>
          <Button
            type="button"
            variant="outline"
            fullWidth
            size="lg"
            className="inline-flex items-center justify-center gap-3 rounded-2xl py-3.5 text-sm font-semibold"
            disabled={pending}
            onClick={() => onOAuth('azure')}
          >
            <MicrosoftOAuthIcon />
            <span>Continue with Microsoft</span>
          </Button>
        </div>

        <div className="relative py-2">
          <div className="absolute inset-0 flex items-center" aria-hidden="true">
            <div className="w-full border-t border-borderSoft/70" />
          </div>
          <div className="relative flex justify-center">
            <span className="bg-background px-3 text-[11px] font-medium uppercase tracking-wider text-textMuted">
              or email
            </span>
          </div>
        </div>

        <form className="space-y-4" onSubmit={onSubmit} noValidate>
          <Input
            label="Email"
            type="email"
            autoComplete="email"
            error={errors.email?.message}
            {...register('email')}
          />
          <Input
            label="Password"
            type="password"
            autoComplete={mode === 'sign_up' ? 'new-password' : 'current-password'}
            error={errors.password?.message}
            {...register('password')}
          />
          {formError ? <p className="text-sm text-danger">{formError}</p> : null}
          {info ? <p className="text-sm text-textSecondary">{info}</p> : null}
          <Button type="submit" fullWidth size="lg" className="rounded-2xl py-3.5 text-sm font-semibold" disabled={pending}>
            {mode === 'sign_in' ? 'Sign in with email' : 'Create account'}
          </Button>
        </form>
      </div>
    </div>
  );
};
